import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Music2, Disc3, ChevronRight, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30 * 1000; // 30s — matches upstream nextsongs.html meta-refresh

/**
 * Clean up the raw song title returned by rumorezero.com. The source sometimes
 * duplicates the artist or appends file-extension hints like ".wmv" or "(1996)".
 * Returns { artist, title } best-effort.
 */
function parseSong(raw) {
  if (!raw) return { artist: "", title: "" };
  // strip common video-file artefacts
  let s = raw.replace(/\s*\.(wmv|mp[34g]|wav|flac|m4a)\b.*$/i, "");
  // collapse double-dashes
  const parts = s.split(/\s+-\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { artist: "", title: raw };

  // de-duplicate consecutive identical pieces ("Bobby Womack - Bobby Womack - Daylight")
  const dedup = [];
  for (const p of parts) {
    if (!dedup.length || dedup[dedup.length - 1].toLowerCase() !== p.toLowerCase()) {
      dedup.push(p);
    }
  }
  if (dedup.length === 1) return { artist: "", title: dedup[0] };
  // Convention: first part = artist, the rest = title
  return { artist: dedup[0], title: dedup.slice(1).join(" — ") };
}

function SongRow({ time, raw, isFirst }) {
  const { artist, title } = useMemo(() => parseSong(raw), [raw]);
  return (
    <li
      data-testid={`upcoming-row${isFirst ? "-next" : ""}`}
      className={`group flex items-center gap-4 p-3.5 sm:p-4 rounded-xl border bg-[#1a1816] transition-colors ${
        isFirst ? "border-[#FF6B00]/60 shadow-[0_0_18px_rgba(255,107,0,0.18)]" : "border-[#332f2b] hover:border-[#FF6B00]/40"
      }`}
    >
      <div className={`shrink-0 w-14 sm:w-16 text-center font-anton text-xl sm:text-2xl tabular-nums ${isFirst ? "text-[#FF6B00]" : "text-[#69635c] group-hover:text-[#A39A8F]"}`}>
        {time}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-oswald uppercase text-base sm:text-lg text-[#F5F2EB] leading-tight truncate">
          {title || raw}
        </div>
        {artist && (
          <div className="font-mono text-[11px] sm:text-xs text-[#A39A8F] truncate mt-0.5">
            {artist}
          </div>
        )}
      </div>
      <Music2 size={16} className="shrink-0 text-[#69635c] group-hover:text-[#FF6B00] transition-colors" />
    </li>
  );
}

export default function UpcomingSongs() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [coverBust, setCoverBust] = useState(Date.now());

  useEffect(() => {
    let stop = false;
    const fetchOnce = async () => {
      // Keep polling even when backgrounded: the artwork cache feeds
      // CarPlay's MediaSession metadata, which must stay accurate while
      // the phone is in standby but the car is playing.
      try {
        // Server sends no-store; the per-request `_` cache-buster below is
        // enough — sending custom request headers would trigger a CORS preflight.
        const r = await axios.get(`${API}/upcoming`, {
          timeout: 10000,
          params: { _: Date.now() },
        });
        if (!stop) {
          setData(r.data);
          setError(null);
          setCoverBust(Date.now()); // force OnAir.jpg refresh on every poll
          // Broadcast to other components (e.g. FavoriteNotifier for early alerts)
          try {
            window.dispatchEvent(new CustomEvent("rrz:upcoming", {
              detail: { songs: r.data?.upcoming || [] },
            }));
          } catch { /* ignore */ }
        }
      } catch (e) {
        if (!stop) setError(String(e?.response?.data?.detail || e.message));
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, REFRESH_MS);
    const onVis = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const onAir = data?.on_air;
  const onAirParsed = useMemo(() => parseSong(onAir?.title), [onAir?.title]);
  const upcoming = data?.upcoming || [];

  return (
    <section
      id="programmi"
      data-testid="upcoming-section"
      className="relative border-t border-[#332f2b] bg-[#0f0d0b]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-3 flex items-center gap-2">
            <Disc3 size={12} className="vinyl-spin" style={{ animationDuration: "8s" }} />
            /04 — Playlist
          </div>
          <h2 className="font-anton uppercase text-4xl sm:text-6xl text-[#F5F2EB] leading-none">
            Prossimi <span className="text-[#FF6B00]">brani in onda</span>
          </h2>
          <p className="mt-4 font-mono text-sm text-[#A39A8F] max-w-xl leading-relaxed">
            Scopri cosa stiamo per mandare in diretta. La playlist si aggiorna
            in tempo reale dalla regia.
          </p>
        </div>

        {error && !data && (
          <div className="rounded-2xl border border-[#cf2020]/40 bg-[#cf2020]/10 p-5 font-mono text-sm text-[#ff8a8a] flex items-center gap-3">
            <AlertTriangle size={18} />
            Playlist non disponibile al momento ({error})
          </div>
        )}

        {data && (
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-10 min-w-0">
            {/* === IN ONDA card === */}
            {onAir && (
              <div
                data-testid="upcoming-on-air"
                className="relative rounded-2xl overflow-hidden border border-[#FF6B00]/40 bg-gradient-to-br from-[#1a1816] via-[#15110d] to-[#1a1816] p-5 sm:p-6 min-w-0"
              >
                <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#FF6B00]/15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00] led-on" />
                    In onda adesso
                  </div>
                  <div className="aspect-square w-full max-w-[280px] mx-auto rounded-xl overflow-hidden bg-[#0a0907] ring-1 ring-[#332f2b] shadow-[0_25px_50px_rgba(0,0,0,0.55)]">
                    {onAir.cover ? (
                      <img
                        src={`${onAir.cover}?t=${coverBust}`}
                        alt={onAir.title}
                        className="w-full h-full object-cover"
                        data-testid="upcoming-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music2 size={48} className="text-[#FF6B00]/60" />
                      </div>
                    )}
                  </div>
                  <div className="mt-5 text-center min-w-0">
                    <div data-testid="on-air-title" className="font-anton uppercase text-xl sm:text-2xl lg:text-3xl text-[#F5F2EB] leading-tight break-words">
                      {onAirParsed.title || onAir.title}
                    </div>
                    {onAirParsed.artist && (
                      <div className="mt-1 font-oswald uppercase text-sm sm:text-base lg:text-lg text-[#FF6B00] break-words">
                        {onAirParsed.artist}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* === UPCOMING list === */}
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-4 flex items-center gap-2">
                <ChevronRight size={12} />
                A seguire · {upcoming.length} {upcoming.length === 1 ? "brano" : "brani"}
              </div>
              {upcoming.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#332f2b] p-10 text-center font-mono text-sm text-[#69635c]">
                  Nessun brano in coda al momento
                </div>
              ) : (
                <ul data-testid="upcoming-list" className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
                  {upcoming.map((s, i) => (
                    <SongRow key={`${s.time}-${i}`} time={s.time} raw={s.title} isFirst={i === 0} />
                  ))}
                </ul>
              )}
              <div className="mt-5 text-center lg:text-left">
                <a
                  href="https://www.rumorezero.com/nextsongs.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="upcoming-full-link"
                  className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#69635c] hover:text-[#FF6B00] transition"
                >
                  Playlist completa su rumorezero.com →
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
